import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { CircleCheck, Info, IconNode, Siren, TriangleAlert } from 'lucide';
import { NbIcon } from '../../icon/icon';

export type BannerVariant = 'info' | 'warning' | 'success' | 'critical';

interface BannerSpec {
  readonly icon: IconNode;
  /** ARIA live role — assertive for warning/critical, polite for info/success. */
  readonly role: 'status' | 'alert';
  /** Visually-hidden status word so the meaning is never colour-only. */
  readonly label: string;
}

const SPECS: Record<BannerVariant, BannerSpec> = {
  info: { icon: Info, role: 'status', label: 'Informasjon' },
  warning: { icon: TriangleAlert, role: 'alert', label: 'Advarsel' },
  success: { icon: CircleCheck, role: 'status', label: 'Vellykket' },
  critical: { icon: Siren, role: 'alert', label: 'Kritisk' },
};

/**
 * `<nb-banner>` — an inline status banner (Mockup `.banner` + variants).
 *
 * A leading status icon (22px), a bold title (`.bt`) and projected body
 * content (`.bd`). The variant drives both the colour scheme AND the icon
 * shape, and a visually-hidden status word is announced first, so the meaning
 * never depends on colour alone (WCAG 1.4.1).
 *
 * `info`/`success` use `role="status"` (polite); `warning`/`critical` use
 * `role="alert"` (assertive).
 *
 * @example
 * <nb-banner variant="warning" title="Vedlikehold pågår">
 *   Tjenesten kan være ustabil frem til kl. 14.
 * </nb-banner>
 */
@Component({
  selector: 'nb-banner',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NbIcon],
  host: {
    class: 'banner',
    '[class]': 'variant()',
    '[attr.role]': 'spec().role',
  },
  template: `<nb-icon [icon]="spec().icon" />
    <span class="nb-sr-only">{{ spec().label }}:</span>
    <div class="bw">
      <div class="bt">{{ title() }}</div>
      <div class="bd"><ng-content /></div>
    </div>`,
  styleUrl: './banner.css',
})
export class NbBanner {
  readonly variant = input<BannerVariant>('info');
  readonly title = input.required<string>();

  protected readonly spec = computed<BannerSpec>(() => SPECS[this.variant()]);
}
